import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { scrollAndFlash } from '../utils/scrollFlash';
import { useAuth } from '../context/AuthContext';
import { PageShell } from '../components/Layout';
import JambTopbar from '../components/JambTopbar';
import { SkeletonGrid } from '../components/Skeleton';
import api, { fmtPostTime, smartTime } from '../utils/api';
import '../styles/jamb.css';
import { IcHeart, IcMsg, IcTrash, IcCamera, IcX, IcChevL, IcChevR } from '../components/Icons';
import { MathText } from '../components/MathInput';
import MiniProfilePopup from '../components/MiniProfilePopup';
import { ReactionButton } from '../components/FeedReactions';
import { useKeyboardInset } from '../hooks/useKeyboardInset';
import { confirmDialog, promptDialog, alertDialog } from '../utils/dialog';

// ── Image Grid ────────────────────────────────────────────────────────────

function ImageGrid({ images, onImageClick }) {
  const n = images.length;
  if (n === 0) return null;
  const style1 = { width: '100%', height: '100%', objectFit: 'cover', display: 'block' };
  if (n === 1) return (
    <div style={{ borderRadius: 12, overflow: 'hidden', cursor: 'pointer', aspectRatio: '16/10', background: '#000' }} onClick={() => onImageClick(0)}>
      <img src={images[0]} alt="" style={style1} />
    </div>
  );
  if (n === 2) return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, borderRadius: 12, overflow: 'hidden' }}>
      {images.map((src, i) => <div key={i} style={{ aspectRatio: '1/1', cursor: 'pointer' }} onClick={() => onImageClick(i)}><img src={src} alt="" style={style1} /></div>)}
    </div>
  );
  if (n === 3) return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gridTemplateRows: '1fr 1fr', gap: 2, borderRadius: 12, overflow: 'hidden', height: 320 }}>
      <div style={{ gridRow: '1 / 3', cursor: 'pointer', overflow: 'hidden' }} onClick={() => onImageClick(0)}><img src={images[0]} alt="" style={style1} /></div>
      {images.slice(1).map((src, i) => <div key={i} style={{ cursor: 'pointer', overflow: 'hidden' }} onClick={() => onImageClick(i + 1)}><img src={src} alt="" style={style1} /></div>)}
    </div>
  );
  if (n === 4) return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, borderRadius: 12, overflow: 'hidden' }}>
      {images.map((src, i) => <div key={i} style={{ aspectRatio: '1/1', cursor: 'pointer', overflow: 'hidden' }} onClick={() => onImageClick(i)}><img src={src} alt="" style={style1} /></div>)}
    </div>
  );
  const extra = n - 5;
  return (
    <div style={{ borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        {images.slice(0, 2).map((src, i) => <div key={i} style={{ aspectRatio: '16/10', cursor: 'pointer', overflow: 'hidden' }} onClick={() => onImageClick(i)}><img src={src} alt="" style={style1} /></div>)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(3, n - 2)}, 1fr)`, gap: 2 }}>
        {images.slice(2, 5).map((src, i) => {
          const isLast = i === Math.min(3, n - 2) - 1 && extra > 0;
          return (
            <div key={i} style={{ aspectRatio: '1/1', position: 'relative', cursor: 'pointer', overflow: 'hidden' }} onClick={() => onImageClick(i + 2)}>
              <img src={src} alt="" style={style1} />
              {isLast && <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 22, color: '#fff' }}>+{extra + 1}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Lightbox ──────────────────────────────────────────────────────────────

function Lightbox({ images, startIdx, onClose }) {
  const [idx, setIdx] = useState(startIdx);
  useEffect(() => {
    const k = e => {
      if (e.key === 'Escape')     onClose();
      if (e.key === 'ArrowLeft')  setIdx(i => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setIdx(i => Math.min(images.length - 1, i + 1));
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [images.length, onClose]);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.96)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <button onClick={onClose} style={{ position: 'absolute', top: 20, right: 20, background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: 44, height: 44, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IcX size={18} /></button>
      {idx > 0 && <button onClick={e => { e.stopPropagation(); setIdx(i => i - 1); }} style={{ position: 'absolute', left: 20, background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: 44, height: 44, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IcChevL size={22} /></button>}
      <img src={images[idx]} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8 }} onClick={e => e.stopPropagation()} />
      {idx < images.length - 1 && <button onClick={e => { e.stopPropagation(); setIdx(i => i + 1); }} style={{ position: 'absolute', right: 20, background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: 44, height: 44, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IcChevR size={22} /></button>}
      <div style={{ position: 'absolute', bottom: 20, color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>{idx + 1} / {images.length}</div>
    </div>
  );
}

// ── Post Comment Modal ────────────────────────────────────────────────────

const IcPencil = () => (
  <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

function PostDetailModal({ post, currentText, isEdited, currentUser, isAdmin, onClose }) {
  // Lift the sheet above the on-screen keyboard (Android 15 edge-to-edge stops
  // the WebView resizing, so the input otherwise hides under the keypad).
  const kbInset = useKeyboardInset();
  const [comments, setComments] = useState([]);
  const [input, setInput]       = useState('');
  const [loading, setLoading]   = useState(true);
  const [replyTo, setReplyTo]   = useState(null); // { _id, author, text } — a comment object
  const [editingId, setEditId]  = useState(null); // { id, isReply, parentId }
  const [editText, setEditText] = useState('');
  const inputRef  = useRef(null);
  const editRef   = useRef(null);

  useEffect(() => {
    api.get(`/posts/${post._id}/comments`)
      .then(r => setComments(r.data.comments || post.comments || []))
      .catch(() => setComments(post.comments || []))
      .finally(() => { setLoading(false); setTimeout(() => inputRef.current?.focus(), 80); });
  }, [post._id]); // eslint-disable-line

  const submit = async e => {
    e.preventDefault();
    if (!input.trim()) return;
    try {
      if (replyTo) {
        const { data } = await api.post(`/posts/${post._id}/comments/${replyTo._id}/reply`, { text: input });
        setComments(p => p.map(c => c._id === replyTo._id ? { ...c, replies: [...(c.replies || []), data.reply] } : c));
      } else {
        const { data } = await api.post(`/posts/${post._id}/comment`, { text: input });
        setComments(p => [...p, { ...data.comment, replies: [] }]);
      }
      setInput(''); setReplyTo(null);
    } catch (e) { alert(e.response?.data?.message || 'Could not post comment.'); }
  };

  const startEdit = (item, isReply, parentId) => {
    setEditId({ id: item._id, isReply, parentId });
    setEditText(item.text);
    setTimeout(() => editRef.current?.focus(), 40);
  };

  const saveEdit = async () => {
    if (!editText.trim() || !editingId) return;
    try {
      if (editingId.isReply) {
        await api.patch(`/posts/${post._id}/comments/${editingId.parentId}/replies/${editingId.id}`, { text: editText });
        setComments(p => p.map(c => c._id === editingId.parentId
          ? { ...c, replies: c.replies.map(r => r._id === editingId.id ? { ...r, text: editText, isEdited: true } : r) }
          : c));
      } else {
        await api.patch(`/posts/${post._id}/comments/${editingId.id}`, { text: editText });
        setComments(p => p.map(c => c._id === editingId.id ? { ...c, text: editText, isEdited: true } : c));
      }
      setEditId(null); setEditText('');
    } catch (e) { alert(e.response?.data?.message || 'Could not save edit.'); }
  };

  const doDelete = async (item, isReply, parentId) => {
    try {
      if (isReply) {
        await api.delete(`/posts/${post._id}/comments/${parentId}/replies/${item._id}`);
        setComments(p => p.map(c => c._id === parentId
          ? { ...c, replies: c.replies.map(r => r._id === item._id ? { ...r, isDeleted: true, text: '[deleted]' } : r) }
          : c));
      } else {
        await api.delete(`/posts/${post._id}/comments/${item._id}`);
        setComments(p => p.map(c => c._id === item._id ? { ...c, isDeleted: true, text: '[deleted]' } : c));
      }
    } catch (e) { alert(e.response?.data?.message || 'Could not delete comment.'); }
  };

  const author = post.author;
  const authorName = author?.fullName || `${author?.firstName || ''} ${author?.lastName || ''}`.trim() || 'Unknown';
  const totalCount = comments.reduce((n, c) => n + 1 + (c.replies?.length || 0), 0);

  const ReplyBubble = ({ r, parentId }) => {
    const rName = r.author?.fullName || `${r.author?.firstName || ''} ${r.author?.lastName || ''}`.trim() || '?';
    const isMine = r.author?._id === currentUser?._id || r.author === currentUser?._id;
    const canAct = isMine || isAdmin;
    const isEditingThis = editingId?.id === r._id && editingId?.isReply;
    return (
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--org)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff', flexShrink: 0, overflow: 'hidden' }}>
          {r.author?.profilePicture ? <img src={r.author.profilePicture} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (rName[0] || '?').toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {isEditingThis ? (
            <div style={{ display: 'flex', gap: 6 }}>
              <input ref={editRef} value={editText} onChange={e => setEditText(e.target.value)} className="form-input" style={{ flex: 1, borderRadius: 99, fontSize: 12, padding: '6px 12px' }} onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditId(null); }} />
              <button className="btn btn-primary btn-sm" style={{ borderRadius: 99, padding: '6px 12px', fontSize: 12 }} onClick={saveEdit}>Save</button>
              <button className="btn btn-ghost btn-sm" style={{ borderRadius: 99, padding: '6px 10px', fontSize: 12 }} onClick={() => setEditId(null)}>Cancel</button>
            </div>
          ) : (
            <div style={{ background: r.isDeleted ? 'transparent' : 'rgba(228,231,236,0.45)', borderRadius: '0 12px 12px 12px', padding: r.isDeleted ? '4px 0' : '7px 12px', border: r.isDeleted ? 'none' : 'none' }}>
              {!r.isDeleted && <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--txt)', marginBottom: 2 }}>{rName}</div>}
              <div style={{ fontSize: 12, color: r.isDeleted ? 'var(--txt-xl)' : 'var(--txt-m)', lineHeight: 1.55, fontStyle: r.isDeleted ? 'italic' : 'normal' }}>
                {r.isDeleted ? 'This reply was deleted.' : r.text}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 3, paddingLeft: 4 }}>
            <span style={{ fontSize: 10, color: 'var(--txt-xl)' }}>{smartTime(r.createdAt)}</span>
            {r.isEdited && !r.isDeleted && <span style={{ fontSize: 10, color: 'var(--txt-xl)', fontStyle: 'italic' }}>Edited</span>}
            {canAct && !r.isDeleted && !isEditingThis && (
              <>
                {isMine && <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--txt-l)', display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 700 }} onClick={() => startEdit(r, true, parentId)}><IcPencil /> Edit</button>}
                <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 700 }} onClick={() => doDelete(r, true, parentId)}><IcTrash size={11} /> {isAdmin && !isMine ? 'Delete (admin)' : 'Delete'}</button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const CommentBubble = ({ c }) => {
    const cName = c.author?.fullName || `${c.author?.firstName || ''} ${c.author?.lastName || ''}`.trim() || '?';
    const isMine = c.author?._id === currentUser?._id || c.author === currentUser?._id;
    const canAct = isMine || isAdmin;
    const isEditingThis = editingId?.id === c._id && !editingId?.isReply;
    const replies = c.replies || [];
    return (
      <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--org)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#fff', overflow: 'hidden', flexShrink: 0 }}>
            {c.author?.profilePicture ? <img src={c.author.profilePicture} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (cName[0] || '?').toUpperCase()}
          </div>
          {replies.length > 0 && (
            <div style={{ width: 2, flex: 1, minHeight: 12, background: 'rgba(255,106,0,0.25)', borderRadius: 99, margin: '4px 0' }} />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {isEditingThis ? (
            <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              <input ref={editRef} value={editText} onChange={e => setEditText(e.target.value)} className="form-input" style={{ flex: 1, borderRadius: 99, fontSize: 13, padding: '8px 14px' }} onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditId(null); }} />
              <button className="btn btn-primary btn-sm" style={{ borderRadius: 99 }} onClick={saveEdit}>Save</button>
              <button className="btn btn-ghost btn-sm" style={{ borderRadius: 99 }} onClick={() => setEditId(null)}>Cancel</button>
            </div>
          ) : (
            <div style={{ background: c.isDeleted ? 'transparent' : 'var(--nat-s)', borderRadius: '0 14px 14px 14px', padding: c.isDeleted ? '4px 0' : '9px 14px' }}>
              {!c.isDeleted && <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--txt)', marginBottom: 2 }}>{cName}</div>}
              <div style={{ fontSize: 13, color: c.isDeleted ? 'var(--txt-xl)' : 'var(--txt-m)', lineHeight: 1.55, fontStyle: c.isDeleted ? 'italic' : 'normal' }}>
                {c.isDeleted ? 'This comment was deleted.' : c.text}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4, paddingLeft: 4 }}>
            <span style={{ fontSize: 10, color: 'var(--txt-xl)' }}>{smartTime(c.createdAt)}</span>
            {c.isEdited && !c.isDeleted && <span style={{ fontSize: 10, color: 'var(--txt-xl)', fontStyle: 'italic' }}>Edited</span>}
            {!c.isDeleted && !isEditingThis && (
              <>
                <button onClick={() => { setReplyTo(c); setInput(''); setTimeout(() => inputRef.current?.focus(), 50); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, color: 'var(--txt-l)', padding: 0 }}
                  onMouseEnter={e => e.currentTarget.style.color = 'var(--org)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--txt-l)'}>
                  Reply
                </button>
                {canAct && (
                  <>
                    {isMine && <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--txt-l)', display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 700 }} onClick={() => startEdit(c, false, null)}><IcPencil /> Edit</button>}
                    <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 700 }} onClick={() => doDelete(c, false, null)}><IcTrash size={12} /> {isAdmin && !isMine ? 'Delete (admin)' : 'Delete'}</button>
                  </>
                )}
              </>
            )}
          </div>
          {replies.length > 0 && (
            <div style={{ marginTop: 8, paddingLeft: 16, position: 'relative' }}>
              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 2, background: 'rgba(255,106,0,0.22)', borderRadius: 99 }} />
              {replies.map((r, i) => <ReplyBubble key={r._id || i} r={r} parentId={c._id} />)}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 0 }} onClick={onClose}>
      <div style={{ background: 'var(--white)', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 600, maxHeight: `calc(88dvh - ${kbInset}px)`, marginBottom: kbInset, transition: 'margin-bottom 0.15s ease', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 0' }}>
          <div style={{ width: 36, height: 4, borderRadius: 99, background: 'var(--nat-d)' }} />
        </div>
        <div style={{ padding: '10px 20px 14px', borderBottom: '1px solid var(--nat-d)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', overflow: 'hidden', background: 'var(--org)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#fff', flexShrink: 0 }}>
            {author?.profilePicture ? <img src={author.profilePicture} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : authorName[0]}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)' }}>{authorName}</div>
            <div style={{ fontSize: 11, color: 'var(--txt-l)' }}>{fmtPostTime(post.createdAt)}{isEdited && <span style={{ marginLeft: 6, fontStyle: 'italic' }}>· Edited</span>}</div>
          </div>
          <button onClick={onClose} style={{ background: 'var(--nat-s)', border: 'none', cursor: 'pointer', color: 'var(--txt-l)', width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IcX size={15} /></button>
        </div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {currentText && <div style={{ padding: '16px 20px', fontSize: 15, color: 'var(--txt)', lineHeight: 1.7, borderBottom: '1px solid var(--nat-s)' }}>{currentText}</div>}
          {post.images?.length > 0 && <div style={{ borderBottom: '1px solid var(--nat-s)' }}><ImageGrid images={post.images} onImageClick={() => {}} /></div>}
          <div style={{ padding: '14px 20px 20px' }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--txt-l)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 14 }}>
              {totalCount > 0 ? `${totalCount} Comment${totalCount !== 1 ? 's' : ''}` : 'No comments yet'}
            </div>
            {loading
              ? <div style={{ textAlign: 'center', padding: 24 }}><div className="spinner" style={{ width: 20, height: 20 }} /></div>
              : comments.map((c, i) => <CommentBubble key={c._id || i} c={c} />)}
          </div>
        </div>
        {replyTo && (
          <div style={{ padding: '8px 16px', background: 'rgba(255,106,0,0.04)', borderTop: '2px solid rgba(255,106,0,0.2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <div style={{ fontSize: 12, color: 'var(--txt-m)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 3, height: 28, background: 'var(--org)', borderRadius: 2 }} />
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--org)', marginBottom: 1 }}>Replying to {replyTo.author?.fullName || replyTo.author?.firstName || '?'}</div>
                <div style={{ color: 'var(--txt-l)', fontSize: 11 }}>{replyTo.text?.slice(0, 50)}{replyTo.text?.length > 50 ? '…' : ''}</div>
              </div>
            </div>
            <button onClick={() => setReplyTo(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--txt-l)', display: 'flex', padding: 4 }}><IcX size={12} /></button>
          </div>
        )}
        <div style={{ padding: '12px 16px', borderTop: '1px solid var(--nat-d)', flexShrink: 0 }}>
          <form onSubmit={submit} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--org)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#fff', flexShrink: 0, overflow: 'hidden' }}>
              {currentUser?.profilePicture ? <img src={currentUser.profilePicture} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (currentUser?.firstName?.[0] || '?')}
            </div>
            <input ref={inputRef} className="form-input" placeholder={replyTo ? `Reply to ${replyTo.author?.firstName || '?'}…` : 'Write a comment…'} value={input} onChange={e => setInput(e.target.value)} onFocus={() => setTimeout(() => inputRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300)} style={{ flex: 1, borderRadius: 99, fontSize: 13, padding: '9px 16px' }} />
            <button type="submit" className="btn btn-primary btn-sm" style={{ borderRadius: 99, padding: '9px 18px' }} disabled={!input.trim()}>Post</button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Post Card ─────────────────────────────────────────────────────────────

const IcEdit = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

function Avatar({ user, size = 40, name }) {
  const n = name || user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || '?';
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', overflow: 'hidden', background: 'var(--org)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#fff', fontSize: Math.round(size * 0.36), flexShrink: 0 }}>
      {user?.profilePicture ? <img src={user.profilePicture} alt={n} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : n[0]?.toUpperCase()}
    </div>
  );
}

// Inline poll: live results, single/multi choice, closed state. Results show
// once you've voted (or the poll closed) — WhatsApp/Twitter behaviour.
function PollView({ postId, poll: initial }) {
  const [poll, setPoll] = useState(initial);
  const [busy, setBusy] = useState(false);
  const voted   = poll.myVotes?.length > 0;
  const showRes = voted || poll.closed;
  const vote = async (i) => {
    if (busy || poll.closed) return;
    setBusy(true);
    try { const { data } = await api.post(`/posts/${postId}/poll/vote`, { optionIndex: i }); setPoll(data.poll); }
    catch (e) { alert(e.response?.data?.message || 'Could not vote.'); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ padding: '2px 16px 14px' }}>
      {poll.options.map(o => {
        const pct = poll.totalVotes ? Math.round((o.votes / poll.totalVotes) * 100) : 0;
        return (
          <button key={o.i} onClick={() => vote(o.i)} disabled={busy || poll.closed}
            style={{ position: 'relative', width: '100%', textAlign: 'left', marginBottom: 8, padding: '10px 14px', borderRadius: 10, cursor: poll.closed ? 'default' : 'pointer', overflow: 'hidden',
              border: `1.5px solid ${o.mine ? 'var(--org)' : 'var(--nat-d)'}`, background: 'var(--white)' }}>
            {showRes && <div style={{ position: 'absolute', inset: 0, width: `${pct}%`, background: o.mine ? 'var(--org-l)' : 'var(--nat-s)', transition: 'width 0.4s' }} />}
            <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, fontSize: 13.5, fontWeight: o.mine ? 800 : 600, color: 'var(--txt)' }}>
              <span>{o.mine && '✓ '}{o.text}</span>
              {showRes && <span style={{ color: 'var(--txt-m)', fontWeight: 700 }}>{pct}%</span>}
            </div>
          </button>
        );
      })}
      <div style={{ fontSize: 11.5, color: 'var(--txt-l)', fontWeight: 600, marginTop: 2 }}>
        {poll.totalVotes} vote{poll.totalVotes !== 1 ? 's' : ''}
        {poll.multi ? ' · choose more than one' : ''}
        {poll.closed ? ' · closed' : (!voted ? ' · tap to vote' : '')}
      </div>
    </div>
  );
}

function PostCard({ post, currentUser, isAdmin, onDelete, onProfileClick, onBookmarkChange }) {
  const navigate = useNavigate();
  const [lightbox, setLightbox]       = useState(null);
  const [comment, setComment]         = useState('');
  const [showComments, setShowC]      = useState(false);
  const [comments, setComments]       = useState(post.comments || []);
  const [rx, setRx] = useState({
    myReaction: post.myReaction ?? (post.likes?.some(id => String(id) === String(currentUser?._id)) ? 'like' : null),
    reactionCounts: post.reactionCounts || (post.likes?.length ? { like: post.likes.length } : {}),
    total: post.totalReactions ?? (post.likes?.length || 0),
  });
  const [bookmarked, setBookmarked]   = useState(!!post.isBookmarked);
  const [commentCount, setCommentCount] = useState(post.commentCount ?? (post.comments?.length || 0));
  const [editing, setEditing]         = useState(false);
  const [editText, setEditText]       = useState(post.text || '');
  const [currentText, setCurrentText] = useState(post.text || '');
  const [isEdited, setIsEdited]       = useState(post.isEdited || false);
  const [showModal, setShowModal]     = useState(false);
  const [expanded, setExpanded]       = useState(false);
  const [showActions, setShowActions] = useState(false);

  const isMine   = post.author?._id === currentUser?._id;
  const canDel   = isMine || isAdmin;
  const TEXT_MAX = 300;
  const needsExpand = currentText.length > TEXT_MAX;
  const displayText = expanded ? currentText : currentText.slice(0, TEXT_MAX);

  const author = post.author;
  const authorName = author?.fullName || `${author?.firstName || ''} ${author?.lastName || ''}`.trim() || 'Unknown';
  const isTutor = author?.role === 'tutor';

  const goProfile = () => {
    if (onProfileClick && author?._id) onProfileClick(author._id);
    else navigate(isTutor ? `/tutor/${author?._id}` : `/student/${author?._id}`);
  };

  const toggleBookmark = async () => {
    setBookmarked(b => !b);
    try { const { data } = await api.post(`/posts/${post._id}/bookmark`); setBookmarked(data.bookmarked); onBookmarkChange?.(post, data.bookmarked); }
    catch { setBookmarked(b => !b); }
  };
  const reportPost = async () => {
    setShowActions(false);
    const reason = await promptDialog({ title: 'Report post', message: 'Why are you reporting this post? (optional)', placeholder: 'Reason', confirmText: 'Report' });
    if (reason === null) return;
    try { await api.post(`/posts/${post._id}/report`, { reason }); alertDialog({ title: 'Reported', message: 'Thanks — this post has been reported to the moderators.' }); }
    catch (e) { alertDialog({ title: 'Could not report', message: e.response?.data?.message || 'Please try again.' }); }
  };
  const addComment = async e => {
    e.preventDefault();
    if (!comment.trim()) return;
    try { const { data } = await api.post(`/posts/${post._id}/comment`, { text: comment }); setComments(p => [...p, data.comment]); setCommentCount(c => c + 1); setComment(''); }
    catch (err) { alert(err.response?.data?.message || 'Could not post comment.'); }
  };
  const saveEdit = async () => {
    if (!editText.trim()) return;
    try {
      const { data } = await api.put(`/posts/${post._id}`, { text: editText });
      setCurrentText(data.post?.text || editText); setIsEdited(true); setEditing(false);
    } catch (err) { alert(err.response?.data?.message || 'Could not update post.'); setEditing(false); }
  };

  return (
    <>
      <div id={`post-${post._id}`} style={{ background: 'var(--white)', borderRadius: 14, border: '1px solid var(--nat-d)', overflow: 'hidden', marginBottom: 8, transition: 'box-shadow 0.18s, border-color 0.18s' }}
        onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 24px rgba(0,0,0,0.08)'; e.currentTarget.style.borderColor = 'var(--nat-dp)'; }}
        onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = 'var(--nat-d)'; }}>

        {/* ── Header ── */}
        <div style={{ padding: '14px 16px 12px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div onClick={goProfile} style={{ cursor: 'pointer', flexShrink: 0 }}>
            <Avatar user={author} size={40} name={authorName} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
              <span onClick={goProfile} style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)', cursor: 'pointer', lineHeight: 1.3 }}>{authorName}</span>
              {isTutor && <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--org)', background: 'var(--org-l)', padding: '2px 7px', borderRadius: 5, border: '1px solid var(--org-m)' }}>Tutor</span>}
            </div>
            <div style={{ fontSize: 11, color: 'var(--txt-xl)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
              <span>{smartTime(post.createdAt)}</span>
              {isEdited && <><span>·</span><span style={{ fontStyle: 'italic' }}>Edited</span></>}
            </div>
          </div>
          {currentUser && (
            <div style={{ position: 'relative' }}>
              <button onClick={() => setShowActions(s => !s)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--txt-xl)', width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.15s, color 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--nat-s)'; e.currentTarget.style.color = 'var(--txt)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--txt-xl)'; }}>
                <svg width={15} height={15} viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="12" cy="19" r="1.8"/></svg>
              </button>
              {showActions && (
                <div style={{ position: 'absolute', top: 38, right: 0, background: 'var(--white)', border: '1px solid var(--nat-d)', borderRadius: 12, boxShadow: '0 8px 28px rgba(0,0,0,0.13)', zIndex: 50, minWidth: 148, overflow: 'hidden' }}>
                  {isMine && (
                    <button onClick={() => { setEditing(true); setEditText(currentText); setShowActions(false); }}
                      style={{ width: '100%', padding: '11px 16px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, fontWeight: 600, color: 'var(--txt)', textAlign: 'left' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--nat-s)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                      <IcEdit size={13} /> Edit post
                    </button>
                  )}
                  {canDel && (
                    <button onClick={() => { onDelete(post._id); setShowActions(false); }}
                      style={{ width: '100%', padding: '11px 16px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, fontWeight: 600, color: 'var(--err)', textAlign: 'left' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--err-l)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                      <IcTrash size={13} /> Delete
                    </button>
                  )}
                  {!isMine && (
                    <button onClick={reportPost}
                      style={{ width: '100%', padding: '11px 16px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, fontWeight: 600, color: 'var(--txt)', textAlign: 'left' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--nat-s)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                      <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M4 15V4a1 1 0 0 1 1-1h11l-2 4 2 4H5"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
                      Report post
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Body ── */}
        {editing ? (
          <div style={{ padding: '0 16px 14px' }}>
            <textarea value={editText} onChange={e => setEditText(e.target.value)} autoFocus
              style={{ width: '100%', border: '1.5px solid var(--org)', borderRadius: 10, padding: '10px 14px', fontSize: 14, fontFamily: 'var(--body)', resize: 'vertical', outline: 'none', boxSizing: 'border-box', minHeight: 80, lineHeight: 1.65, background: 'var(--nat-s)', color: 'var(--txt)' }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={saveEdit} disabled={!editText.trim()}>Save</button>
              <button className="btn btn-ghost btn-sm" onClick={() => { setEditing(false); setEditText(currentText); }}>Cancel</button>
            </div>
          </div>
        ) : currentText ? (
          <div style={{ padding: '0 16px 14px', fontSize: 14.5, color: 'var(--txt)', lineHeight: 1.72, cursor: 'pointer' }} onClick={() => setShowModal(true)}>
            {displayText}
            {needsExpand && !expanded && '… '}
            {needsExpand && (
              <button onClick={e => { e.stopPropagation(); setExpanded(v => !v); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--org)', fontWeight: 700, fontSize: 13, padding: 0 }}>
                {expanded ? ' Show less' : 'See more'}
              </button>
            )}
          </div>
        ) : null}

        {/* ── Poll ── */}
        {post.poll && <PollView postId={post._id} poll={post.poll} />}

        {/* ── Images ── */}
        {post.images?.length > 0 && (
          <div style={{ padding: '0 0 2px', cursor: 'pointer' }} onClick={() => setLightbox(0)}>
            <ImageGrid images={post.images} onImageClick={setLightbox} />
          </div>
        )}

        {/* ── Actions ── */}
        <div style={{ padding: '4px 10px 8px', display: 'flex', alignItems: 'center', gap: 2, borderTop: '1px solid var(--nat-d)', marginTop: post.images?.length > 0 ? 8 : 0 }}>
          <ReactionButton postId={post._id} myReaction={rx.myReaction} reactionCounts={rx.reactionCounts} total={rx.total} onChange={setRx} />
          <button onClick={() => setShowModal(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--txt-l)', padding: '7px 12px', borderRadius: 10, transition: 'background 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--nat-s)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
            <IcMsg size={16} />
            {commentCount > 0 ? <span>{commentCount}</span> : <span style={{ fontSize: 12, color: 'var(--txt-xl)' }}>Comment</span>}
          </button>
          <button onClick={toggleBookmark} title={bookmarked ? 'Saved' : 'Save post'} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: bookmarked ? 'var(--org)' : 'var(--txt-l)', padding: '7px 12px', borderRadius: 10, transition: 'background 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--nat-s)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
            <svg width={16} height={16} viewBox="0 0 24 24" fill={bookmarked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
          </button>
        </div>
      </div>
      {lightbox !== null && <Lightbox images={post.images} startIdx={lightbox} onClose={() => setLightbox(null)} />}
      {showModal && <PostDetailModal post={post} currentText={currentText} isEdited={isEdited} currentUser={currentUser} isAdmin={isAdmin} onClose={() => setShowModal(false)} />}
    </>
  );
}

// ── QA Card ───────────────────────────────────────────────────────────────

function QACard({ question }) {
  const navigate = useNavigate();
  const author = question.author;
  const authorName = author?.fullName || `${author?.firstName || ''} ${author?.lastName || ''}`.trim() || 'Anonymous';
  const body = question.text || question.content || question.body || '';
  const answerCount = question.comments?.length || question.answerCount || 0;
  return (
    <div onClick={() => navigate('/qa-forum')} style={{ background: 'var(--white)', borderRadius: 14, border: '1px solid var(--nat-d)', overflow: 'hidden', marginBottom: 8, cursor: 'pointer', transition: 'box-shadow 0.15s' }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.07)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
      <div style={{ padding: '13px 16px 10px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <Avatar user={author} size={36} name={authorName} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--txt)' }}>{authorName}</span>
            <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', background: 'var(--org)', color: '#fff', borderRadius: 4, padding: '2px 6px' }}>Q&A</span>
            {question.course && <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--org)', background: 'var(--org-l)', border: '1px solid var(--org-m)', padding: '1px 7px', borderRadius: 4 }}>{question.course}</span>}
          </div>
          <div style={{ fontSize: 11, color: 'var(--txt-l)', marginTop: 1 }}>{fmtPostTime(question.createdAt)}</div>
        </div>
      </div>
      <div style={{ padding: '0 16px 12px', fontSize: 14, color: 'var(--txt)', lineHeight: 1.6, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical' }}>
        <MathText text={body} />
      </div>
      <div style={{ padding: '10px 16px', borderTop: '1px solid var(--nat-d)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, color: 'var(--txt-l)', display: 'flex', alignItems: 'center', gap: 5 }}>
          <IcMsg size={13} />
          {answerCount > 0 ? `${answerCount} answer${answerCount !== 1 ? 's' : ''}` : 'No answers yet'}
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--org)' }}>Answer →</span>
      </div>
    </div>
  );
}

// ── Pinned Announcement ───────────────────────────────────────────────────

const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;
function getSeenAnnIds() { try { return new Set(JSON.parse(localStorage.getItem('tt_seen_ann') || '[]')); } catch { return new Set(); } }
function markAnnSeen(id) { try { const s = getSeenAnnIds(); s.add(id); localStorage.setItem('tt_seen_ann', JSON.stringify([...s])); } catch {} }

// ── Composer ─────────────────────────────────────────────────────────────

function Composer({ onPost, sharedPhotoUrl }) {
  const { currentUser, avatarInitials } = useAuth();
  const [open, setOpen]         = useState(false);
  const [text, setText]         = useState('');
  const [images, setImages]     = useState([]);
  const [previews, setPreviews] = useState([]);
  const [posting, setPosting]   = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef();

  // ── @mentions ── typing "@name" at the end of the text opens a picker;
  // picking inserts the display name and records the user id for the backend.
  const [mentionHits, setMentionHits] = useState([]);
  const [mentioned, setMentioned]     = useState([]); // [{_id, name}]
  const [poll, setPoll]               = useState(null); // null | { options: string[], multi }
  const mentionTimer = useRef(null);
  const onTextChange = (val) => {
    setText(val);
    const m = val.match(/@([A-Za-z][\w'-]{1,24})$/);
    clearTimeout(mentionTimer.current);
    if (!m) { setMentionHits([]); return; }
    mentionTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/users/search?q=${encodeURIComponent(m[1])}&limit=5`);
        setMentionHits(data.users || []);
      } catch { setMentionHits([]); }
    }, 250);
  };
  const pickMention = (u) => {
    const name = u.preferredName || u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim();
    setText(t => t.replace(/@([A-Za-z][\w'-]{1,24})$/, `@${name} `));
    setMentioned(p => p.some(x => x._id === u._id) ? p : [...p, { _id: u._id, name }]);
    setMentionHits([]);
  };

  useEffect(() => {
    if (sharedPhotoUrl) { setPreviews([{ url: sharedPhotoUrl, isShared: true }]); setOpen(true); }
  }, [sharedPhotoUrl]);

  const handleFiles = e => {
    const files = Array.from(e.target.files).slice(0, 10 - images.length);
    setImages(p => [...p, ...files].slice(0, 10));
    files.forEach(f => { const r = new FileReader(); r.onload = ev => setPreviews(p => [...p, ev.target.result].slice(0, 10)); r.readAsDataURL(f); });
  };
  const removeImage = i => { setImages(p => p.filter((_, j) => j !== i)); setPreviews(p => p.filter((_, j) => j !== i)); };

  const pollValid = poll && poll.options.filter(o => o.trim()).length >= 2;
  const submit = async () => {
    const hasShared = previews.some(p => p?.isShared);
    if (poll && !text.trim()) { alert('A poll needs a question — type it above.'); return; }
    if (poll && !pollValid)   { alert('Add at least two poll options.'); return; }
    if (!text.trim() && images.length === 0 && !hasShared && !poll) return;
    setPosting(true); setProgress(0);
    try {
      const fd = new FormData();
      fd.append('text', text);
      images.forEach(f => fd.append('images', f));
      if (hasShared && sharedPhotoUrl) fd.append('sharedImageUrl', sharedPhotoUrl);
      if (poll && pollValid) {
        fd.append('poll', JSON.stringify({ options: poll.options.filter(o => o.trim()).map(text => ({ text })), multi: !!poll.multi }));
      }
      // Only send ids whose @Name is still present (user may have deleted a tag).
      const liveMentions = mentioned.filter(m => text.includes(`@${m.name}`)).map(m => m._id);
      if (liveMentions.length) fd.append('mentions', JSON.stringify(liveMentions));
      const { data } = await api.post('/posts', fd, { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress: pe => setProgress(Math.round((pe.loaded / pe.total) * 100)) });
      onPost(data.post);
      setText(''); setImages([]); setPreviews([]); setMentioned([]); setMentionHits([]); setPoll(null); setOpen(false);
    } catch (e) { alert(e.response?.data?.message || 'Could not post.'); }
    finally { setPosting(false); setProgress(0); }
  };

  return (
    <div style={{ background: 'var(--white)', borderRadius: 16, border: '1px solid var(--nat-d)', marginBottom: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
      {/* Collapsed trigger */}
      {!open ? (
        <div>
          {/* top row: avatar + prompt */}
          <div style={{ padding: '14px 16px 12px', display: 'flex', gap: 11, alignItems: 'center', cursor: 'pointer' }} onClick={() => setOpen(true)}>
            <Avatar user={currentUser} size={40} name={avatarInitials} />
            <div style={{ flex: 1, padding: '11px 16px', borderRadius: 12, background: 'var(--nat-s)', border: '1.5px solid var(--nat-d)', fontSize: 14, color: 'var(--txt-l)', userSelect: 'none', lineHeight: 1.3 }}>
              What's on your mind?
            </div>
          </div>
          {/* action row */}
          <div style={{ display: 'flex', borderTop: '1px solid var(--nat-d)', padding: '0 8px 2px' }}>
            <button style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '10px 8px', background: 'none', border: 'none', cursor: 'pointer', borderRadius: 10, color: 'var(--txt-m)', fontSize: 13, fontWeight: 600, transition: 'background 0.12s' }}
              onClick={() => { fileRef.current.click(); }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--nat-s)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}>
              <IcCamera size={17} style={{ color: '#10b981' }} /> Photo
            </button>
            <div style={{ width: 1, background: 'var(--nat-d)', margin: '8px 0' }} />
            <button style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '10px 8px', background: 'none', border: 'none', cursor: 'pointer', borderRadius: 10, color: 'var(--txt-m)', fontSize: 13, fontWeight: 600, transition: 'background 0.12s' }}
              onClick={() => setOpen(true)}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--nat-s)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
              Write
            </button>
          </div>
          <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => { handleFiles(e); setOpen(true); }} />
        </div>
      ) : (
        <div style={{ padding: '14px 16px' }}>
          <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
            <Avatar user={currentUser} size={38} name={avatarInitials} />
            <div style={{ flex: 1, position: 'relative' }}>
              <textarea value={text} onChange={e => onTextChange(e.target.value)} autoFocus
                placeholder="What's on your mind? Type @ to mention someone"
                style={{ width: '100%', border: 'none', outline: 'none', resize: 'none', fontFamily: 'var(--body)', fontSize: 15, background: 'transparent', color: 'var(--txt)', minHeight: 72, lineHeight: 1.65, boxSizing: 'border-box' }}
                rows={3} />
              {mentionHits.length > 0 && (
                <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', background: 'var(--white)', border: '1px solid var(--nat-d)', borderRadius: 12, boxShadow: '0 8px 28px rgba(0,0,0,0.14)', zIndex: 80, overflow: 'hidden' }}>
                  {mentionHits.map(u => {
                    const nm = u.preferredName || u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim();
                    return (
                      <button key={u._id} onClick={() => pickMention(u)}
                        style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '9px 12px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--nat-s)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                        <Avatar user={u} size={26} name={nm} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--txt)' }}>{nm}</div>
                          <div style={{ fontSize: 10.5, color: 'var(--txt-l)' }}>{u.tutorialId || u.role}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          {previews.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10, paddingLeft: 50 }}>
              {previews.map((src, i) => (
                <div key={i} style={{ position: 'relative' }}>
                  <img src={typeof src === 'string' ? src : src?.url} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 10 }} />
                  <button onClick={() => removeImage(i)} style={{ position: 'absolute', top: -6, right: -6, background: '#111', border: '2px solid var(--white)', borderRadius: '50%', width: 22, height: 22, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}><IcX size={10} /></button>
                </div>
              ))}
            </div>
          )}
          {poll && (
            <div style={{ marginTop: 12, marginLeft: 50, padding: 12, border: '1px solid var(--nat-d)', borderRadius: 12, background: 'var(--nat-s)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--txt-m)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Poll options</span>
                <button onClick={() => setPoll(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--txt-l)', fontSize: 12, fontWeight: 600 }}>Remove poll</button>
              </div>
              {poll.options.map((opt, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center' }}>
                  <input value={opt} onChange={e => setPoll(p => ({ ...p, options: p.options.map((o, j) => j === i ? e.target.value : o) }))}
                    placeholder={`Option ${i + 1}`} maxLength={120}
                    style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--nat-d)', background: 'var(--white)', fontSize: 13, color: 'var(--txt)', outline: 'none' }} />
                  {poll.options.length > 2 && (
                    <button onClick={() => setPoll(p => ({ ...p, options: p.options.filter((_, j) => j !== i) }))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--txt-l)', padding: 4, display: 'flex' }}><IcX size={13} /></button>
                  )}
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                {poll.options.length < 6
                  ? <button onClick={() => setPoll(p => ({ ...p, options: [...p.options, ''] }))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--org)', fontSize: 13, fontWeight: 700 }}>+ Add option</button>
                  : <span />}
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--txt-m)', fontWeight: 600, cursor: 'pointer' }}>
                  <input type="checkbox" checked={poll.multi} onChange={e => setPoll(p => ({ ...p, multi: e.target.checked }))} style={{ accentColor: 'var(--org)', width: 14, height: 14 }} />
                  Allow multiple choices
                </label>
              </div>
            </div>
          )}
          {posting && (
            <div style={{ marginTop: 10, paddingLeft: 50 }}>
              <div style={{ height: 3, borderRadius: 99, background: 'var(--nat-d)', overflow: 'hidden' }}>
                <div style={{ height: '100%', borderRadius: 99, background: 'var(--org)', width: `${progress}%`, transition: 'width 0.3s' }} />
              </div>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--nat-d)' }}>
            <div style={{ display: 'flex', gap: 4 }}>
              <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleFiles} disabled={images.length >= 10} />
              <button onClick={() => fileRef.current.click()} disabled={images.length >= 10}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--txt-l)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, padding: '7px 12px', borderRadius: 10, transition: 'background 0.1s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--nat-s)'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                <IcCamera size={16} /> Photo {images.length > 0 && `(${images.length}/10)`}
              </button>
              <button onClick={() => setPoll(p => p ? null : { options: ['', ''], multi: false })}
                style={{ background: poll ? 'var(--org-l)' : 'none', border: 'none', cursor: 'pointer', color: poll ? 'var(--org)' : 'var(--txt-l)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, padding: '7px 12px', borderRadius: 10, transition: 'background 0.1s' }}
                onMouseEnter={e => { if (!poll) e.currentTarget.style.background = 'var(--nat-s)'; }}
                onMouseLeave={e => { if (!poll) e.currentTarget.style.background = 'none'; }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
                Poll
              </button>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => { setOpen(false); setText(''); setImages([]); setPreviews([]); setPoll(null); }}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={submit} disabled={posting || (!text.trim() && images.length === 0 && !poll)} style={{ borderRadius: 99, padding: '8px 20px' }}>
                {posting ? 'Posting…' : 'Post'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
  return a;
}
function mergeFeed(posts, qa) {
  return [...posts.map(p => ({ ...p, __type: 'post' })), ...qa.map(q => ({ ...q, __type: 'qa' }))]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
}

// ── Sidebar Widget ────────────────────────────────────────────────────────

function SideWidget({ children, style }) {
  return (
    <div style={{ background: 'var(--white)', borderRadius: 16, border: '1px solid var(--nat-d)', overflow: 'hidden', ...style }}>
      {children}
    </div>
  );
}

// ── JAMB Shell ───────────────────────────────────────────────────────────
// JAMB students share this feed page but must keep the JAMB chrome (top nav)
// instead of the undergrad PageShell, which renders the orange sidebar +
// mobile bottom nav bar. .jb-feed-override remaps --org → the JAMB accent.

function JambFeedShell({ children }) {
  return (
    <div className="jb-shell jb-feed-override">
      <JambTopbar />
      <div className="jb-page" style={{ maxWidth: 860, margin: '0 auto', padding: '28px 24px 64px' }}>
        {children}
      </div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────

export default function NewsFeed() {
  const navigate = useNavigate();
  const { currentUser, isAdmin, isStudent, isTutor, isJambStudent } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const containerRef = useRef(null);
  const [containerW, setContainerW] = useState(9999);
  useEffect(() => {
    const el = containerRef.current; if (!el) return;
    const ro = new ResizeObserver(([e]) => setContainerW(e.contentRect.width));
    ro.observe(el); return () => ro.disconnect();
  }, []);
  const isMobile = containerW < 680;

  const [posts, setPosts]             = useState([]);
  const [qaItems, setQaItems]         = useState([]);
  const [feedItems, setFeedItems]     = useState([]);
  const [loading, setLoading]         = useState(true);

  // Deep-link from a notification: scroll to & flash the exact post.
  useEffect(() => {
    const pid = searchParams.get('post');
    if (!pid || loading) return;
    scrollAndFlash(`post-${pid}`);
    setSearchParams(prev => { const p = new URLSearchParams(prev); p.delete('post'); return p; }, { replace: true });
  }, [loading, searchParams]); // eslint-disable-line
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore]         = useState(true);
  const [sharedPhotoUrl, setSharedPhotoUrl] = useState('');
  const [announcements, setAnns]      = useState([]);
  const [pinnedAnns, setPinnedAnns]   = useState([]);
  const [expandedAnns, setExpandedAnns] = useState(new Set());
  const sentinelRef = useRef(null);
  const pageRef     = useRef(1);

  useEffect(() => { const u = searchParams.get('sharePhoto'); if (u) { setSharedPhotoUrl(u); setSearchParams({}, { replace: true }); } }, []);

  useEffect(() => {
    Promise.all([
      api.get('/posts?feed=weighted&page=1&limit=10').catch(() => ({ data: { posts: [] } })),
      isStudent
        ? api.get('/questions?limit=10&sort=recent').catch(() => ({ data: { questions: [] } }))
        : Promise.resolve({ data: { questions: [] } }),
    ]).then(([pr, qr]) => {
      // No client-side shuffle: the backend order is deterministic so infinite
      // scroll never repeats or skips posts.
      const loadedPosts = pr.data.posts || [];
      const loadedQa   = isStudent ? (qr.data.questions || []) : [];
      if (loadedPosts.length < 10) setHasMore(false);
      setPosts(loadedPosts); setQaItems(loadedQa);
      setFeedItems(mergeFeed(loadedPosts, loadedQa));
    }).finally(() => setLoading(false));

    api.get('/announcements?limit=6').then(r => {
      const all = r.data.announcements || [];
      setAnns(all.slice(0, 3));
      const seen = getSeenAnnIds(); const now = Date.now();
      setPinnedAnns(all.filter(a => !seen.has(a._id) && (now - new Date(a.createdAt).getTime()) < THREE_DAYS_MS));
    }).catch(() => {});

  }, [isStudent]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const obs = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loadingMore) {
        pageRef.current += 1; const pg = pageRef.current;
        setLoadingMore(true);
        api.get(`/posts?feed=weighted&page=${pg}&limit=10`).then(({ data }) => {
          const incoming = data.posts || [];
          if (incoming.length < 10) setHasMore(false);
          setPosts(p => {
            const seen = new Set(p.map(x => String(x._id)));
            const fresh = incoming.filter(x => !seen.has(String(x._id))); // guard: page boundaries shift when new posts land
            const next = [...p, ...fresh];
            setFeedItems(mergeFeed(next, qaItems));
            return next;
          });
        }).catch(() => {}).finally(() => setLoadingMore(false));
      }
    }, { rootMargin: '200px' });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [hasMore, loadingMore, qaItems]);

  // ── "New posts" pill ── poll for posts newer than anything on screen and
  // offer a one-tap refresh (Facebook-style) instead of silently reordering.
  // Declared before onPost, which reads it.
  const newestSeenRef = useRef(0);

  const onPost = p => { setPosts(prev => [p, ...prev]); setFeedItems(prev => [{ ...p, __type: 'post' }, ...prev]); newestSeenRef.current = Math.max(newestSeenRef.current, new Date(p.createdAt || Date.now()).getTime()); };
  const [newPostsReady, setNewPostsReady] = useState(false);
  useEffect(() => {
    if (posts.length) {
      newestSeenRef.current = Math.max(newestSeenRef.current, ...posts.map(p => new Date(p.createdAt).getTime()));
    }
  }, [posts]);
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const { data } = await api.get('/posts?page=1&limit=1');
        const latest = data.posts?.[0];
        if (!latest || !newestSeenRef.current) return;
        const ts = new Date(latest.createdAt).getTime();
        if (ts > newestSeenRef.current && latest.author?._id !== currentUser?._id) setNewPostsReady(true);
      } catch { /* offline — try next tick */ }
    }, 45000);
    return () => clearInterval(t);
  }, [currentUser?._id]);
  const refreshFeed = async () => {
    setNewPostsReady(false);
    pageRef.current = 1;
    try {
      const { data } = await api.get('/posts?feed=weighted&page=1&limit=10');
      const loaded = data.posts || [];
      setHasMore(loaded.length >= 10);
      setPosts(loaded);
      setFeedItems(mergeFeed(loaded, qaItems));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch { /* keep current feed */ }
  };
  const deletePost = async id => {
    if (!await confirmDialog({ title: 'Delete post?', message: 'This permanently removes your post and its comments.', confirmText: 'Delete', danger: true })) return;
    try { await api.delete(`/posts/${id}`); setPosts(p => p.filter(x => x._id !== id)); setFeedItems(f => f.filter(x => x._id !== id)); }
    catch (e) { alertDialog({ title: 'Could not delete', message: e.response?.data?.message || 'Please try again.' }); }
  };
  const [miniProfileId, setMiniProfileId] = useState(null);

  // ── Saved posts (right-side drawer) ──
  const [savedOpen, setSavedOpen]       = useState(false);
  const [savedPosts, setSavedPosts]     = useState(null); // null = not yet loaded
  const [savedLoading, setSavedLoading] = useState(false);
  const openSaved = () => {
    setSavedOpen(true);
    if (savedPosts === null) {
      setSavedLoading(true);
      api.get('/posts/bookmarks').then(r => setSavedPosts(r.data.posts || [])).catch(() => setSavedPosts([])).finally(() => setSavedLoading(false));
    }
  };
  const onBookmarkChange = (post, bookmarked) => {
    setSavedPosts(prev => {
      if (prev === null) return prev;
      return bookmarked
        ? [{ ...post, isBookmarked: true }, ...prev.filter(p => p._id !== post._id)]
        : prev.filter(p => p._id !== post._id);
    });
  };

  const inner = (
    <>
      {miniProfileId && <MiniProfilePopup userId={miniProfileId} onClose={() => setMiniProfileId(null)} />}
      <div ref={containerRef}>
        {/* ── New posts pill ── */}
        {newPostsReady && (
          <button onClick={refreshFeed} style={{ position: 'fixed', top: 74, left: '50%', transform: 'translateX(-50%)', zIndex: 150, display: 'flex', alignItems: 'center', gap: 7, background: 'var(--org)', color: '#fff', border: 'none', borderRadius: 999, padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 6px 20px rgba(255,106,0,0.35)' }}>
            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>
            New posts
          </button>
        )}

        {/* ── Saved posts: floating trigger + right-side drawer ── */}
        <button onClick={openSaved} title="Saved posts" style={{ position: 'fixed', right: 18, top: 96, zIndex: 140, width: 44, height: 44, borderRadius: '50%', background: 'var(--white)', border: '1px solid var(--nat-d)', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--org)' }}>
          <svg width={18} height={18} viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
        </button>
        {savedOpen && <div onClick={() => setSavedOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 300 }} />}
        <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(380px, 92vw)', background: 'var(--white)', borderLeft: '1px solid var(--nat-d)', boxShadow: '-8px 0 32px rgba(0,0,0,0.16)', zIndex: 310, transform: savedOpen ? 'translateX(0)' : 'translateX(105%)', transition: 'transform 0.28s cubic-bezier(0.4,0,0.2,1)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--nat-d)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 16, color: 'var(--txt)' }}>Saved Posts</div>
            <button onClick={() => setSavedOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--txt-l)', padding: 6 }}><IcX size={16} /></button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px' }}>
            {savedLoading ? <div className="spinner-wrap"><div className="spinner" /></div>
              : (savedPosts?.length || 0) === 0
                ? <div style={{ textAlign: 'center', color: 'var(--txt-l)', fontSize: 13, padding: '40px 16px' }}>No saved posts yet. Tap the bookmark on any post to save it here.</div>
                : savedPosts.map(sp => {
                    const a = sp.author; const nm = a?.fullName || `${a?.firstName || ''} ${a?.lastName || ''}`.trim() || 'Unknown';
                    return (
                      <div key={sp._id} style={{ border: '1px solid var(--nat-d)', borderRadius: 12, padding: '12px 14px', marginBottom: 10, background: 'var(--nat-s)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <Avatar user={a} size={26} name={nm} />
                          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--txt)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nm}</div>
                          <button title="Remove" onClick={() => { api.post(`/posts/${sp._id}/bookmark`).catch(() => {}); setSavedPosts(p => p.filter(x => x._id !== sp._id)); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--org)', padding: 4, display: 'flex' }}>
                            <svg width={15} height={15} viewBox="0 0 24 24" fill="currentColor"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
                          </button>
                        </div>
                        {sp.text && <div style={{ fontSize: 13, color: 'var(--txt-m)', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{sp.text}</div>}
                        {sp.images?.[0] && <img src={sp.images[0]} alt="" style={{ width: '100%', maxHeight: 120, objectFit: 'cover', borderRadius: 8, marginTop: 8 }} onError={e => e.target.style.display = 'none'} />}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 8, fontSize: 11, color: 'var(--txt-l)' }}>
                          <span>{sp.totalReactions || 0} reactions</span>
                          <span>{sp.commentCount || 0} comments</span>
                          <button onClick={() => { setSavedOpen(false); setSearchParams({ post: sp._id }); }} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--org)', fontWeight: 700, fontSize: 12 }}>View →</button>
                        </div>
                      </div>
                    );
                  })}
          </div>
        </div>

        {/* ── Pinned Announcements ── */}
        {pinnedAnns.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            {pinnedAnns.map(a => (
              <div key={a._id} ref={() => markAnnSeen(a._id)}
                style={{ background: 'linear-gradient(135deg, rgba(255,106,0,0.05), rgba(255,106,0,0.01))', border: '1px solid rgba(255,106,0,0.18)', borderRadius: 14, padding: '12px 16px', marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--org-l)', border: '1px solid var(--org-m)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="var(--org)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="2" x2="22" y2="6"/><path d="M7.5 20.5 19 9l-5-5L2.5 15.5z"/></svg>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--txt)', marginBottom: 2 }}>{a.title}</div>
                  {a.body && (
                    <>
                      <div style={{ fontSize: 12, color: 'var(--txt-m)', lineHeight: 1.55, ...(expandedAnns.has(a._id) ? {} : { overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }) }}><MathText text={a.body} /></div>
                      {a.body.length > 200 && (
                        <button onClick={() => setExpandedAnns(p => { const n = new Set(p); n.has(a._id) ? n.delete(a._id) : n.add(a._id); return n; })}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--org)', fontWeight: 700, fontSize: 11.5, padding: '2px 0 0' }}>
                          {expandedAnns.has(a._id) ? 'Show less' : 'See more'}
                        </button>
                      )}
                    </>
                  )}
                  <div style={{ fontSize: 10, color: 'var(--txt-l)', marginTop: 4 }}>{fmtPostTime(a.createdAt)}</div>
                </div>
                <button onClick={() => setPinnedAnns(p => p.filter(x => x._id !== a._id))}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--txt-l)', padding: 4, flexShrink: 0, borderRadius: '50%' }}>
                  <IcX size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* ── Main grid ── */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 290px', gap: 20, alignItems: 'start' }}>
          {/* Feed column */}
          <div>
            <Composer onPost={onPost} sharedPhotoUrl={sharedPhotoUrl} />
            {loading
              ? <SkeletonGrid count={4} minWidth={520} lines={3} />
              : feedItems.length === 0
                ? (
                  <div style={{ background: 'var(--white)', borderRadius: 16, border: '1px solid var(--nat-d)', padding: '48px 24px', textAlign: 'center' }}>
                    <div style={{ fontSize: 32, marginBottom: 12 }}>
                      <svg width={40} height={40} viewBox="0 0 24 24" fill="none" stroke="var(--nat-d)" strokeWidth={1.5}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--txt)', marginBottom: 6 }}>No posts yet</div>
                    <div style={{ fontSize: 13, color: 'var(--txt-l)' }}>Be the first to share something with the community.</div>
                  </div>
                )
                : feedItems.map(item =>
                    item.__type === 'qa'
                      ? <QACard key={`qa-${item._id}`} question={item} />
                      : <PostCard key={item._id} post={item} currentUser={currentUser} isAdmin={isAdmin} onDelete={deletePost} onProfileClick={setMiniProfileId} onBookmarkChange={onBookmarkChange} />
                  )
            }
            <div ref={sentinelRef} style={{ height: 1 }} />
            {loadingMore && <div style={{ textAlign: 'center', padding: 20 }}><div className="spinner" style={{ width: 22, height: 22 }} /></div>}
            {!hasMore && feedItems.length > 0 && (
              <div style={{ textAlign: 'center', padding: '20px 0', fontSize: 12, color: 'var(--txt-l)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                <div style={{ flex: 1, height: 1, background: 'var(--nat-d)' }} />
                <span>You're all caught up</span>
                <div style={{ flex: 1, height: 1, background: 'var(--nat-d)' }} />
              </div>
            )}
          </div>

          {/* Sidebar (desktop only) */}
          {!isMobile && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Role CTA */}
              {isStudent ? (
                <div style={{ background: 'linear-gradient(135deg, var(--org) 0%, var(--org-xd) 100%)', borderRadius: 16, padding: '20px 18px', color: '#fff', boxShadow: '0 4px 20px rgba(255,106,0,0.2)' }}>
                  <div style={{ fontFamily: 'var(--display)', fontSize: 15, fontWeight: 800, marginBottom: 6 }}>Ready to Test Yourself?</div>
                  <div style={{ fontSize: 12, opacity: 0.85, lineHeight: 1.6, marginBottom: 14 }}>Take a quiz from your enrolled courses and track your score.</div>
                  <button style={{ width: '100%', background: '#fff', color: 'var(--org)', border: 'none', borderRadius: 10, padding: '9px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer' }} onClick={() => navigate('/quiz')}>Start Quiz</button>
                </div>
              ) : (
                <div style={{ background: 'linear-gradient(145deg, #0c1428 0%, #152340 100%)', borderRadius: 16, padding: '20px 18px', color: '#fff', border: '1px solid rgba(255,255,255,0.06)', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(255,106,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="var(--org)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                    </div>
                    <div style={{ fontFamily: 'var(--display)', fontSize: 15, fontWeight: 800 }}>Tutor Hub</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
                    {[
                      { label: 'My Courses', value: (currentUser?.courses || []).length, path: '/question-pool' },
                      { label: 'Question Pool', value: 'Open', path: '/question-pool' },
                    ].map(s => (
                      <button key={s.label} onClick={() => navigate(s.path)} style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '10px 0', cursor: 'pointer', textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--org)', fontFamily: 'var(--display)' }}>{s.value}</div>
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>{s.label}</div>
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={{ flex: 1, background: 'var(--org)', color: '#fff', border: 'none', borderRadius: 10, padding: '9px 0', fontSize: 12, fontWeight: 700, cursor: 'pointer' }} onClick={() => navigate('/tutor-dashboard')}>Dashboard</button>
                    <button style={{ flex: 1, background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, padding: '9px 0', fontSize: 12, fontWeight: 700, cursor: 'pointer' }} onClick={() => navigate('/course-manager')}>Course Manager</button>
                  </div>
                </div>
              )}

              {/* Announcements */}
              {announcements.length > 0 && (
                <SideWidget>
                  <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--nat-d)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 'var(--fs-h2)', fontWeight: 800, color: 'var(--txt)' }}>Announcements</span>
                    <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => navigate('/announcements')}>See all →</button>
                  </div>
                  <div style={{ padding: '8px 0' }}>
                    {announcements.map((a, i) => (
                      <div key={a._id} style={{ padding: '10px 16px', borderBottom: i < announcements.length - 1 ? '1px solid var(--nat-d)' : 'none', cursor: 'pointer' }} onClick={() => navigate('/announcements')}>
                        <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 700, color: 'var(--txt)', marginBottom: 3, lineHeight: 1.35 }}>{a.title}</div>
                        <div style={{ fontSize: 'var(--fs-meta)', color: 'var(--txt-l)' }}>{fmtPostTime(a.createdAt)}</div>
                      </div>
                    ))}
                  </div>
                </SideWidget>
              )}

            </div>
          )}
        </div>
      </div>
    </>
  );

  if (isJambStudent) return <JambFeedShell>{inner}</JambFeedShell>;
  return <PageShell title="News Feed">{inner}</PageShell>;
}
